<script setup lang="ts">
import hashString from "~/utilities/hash"

const products = useProductVariantsStore()
const content = useProductContentStore()
const route = useRoute<"article">()
const router = useRouter()
const validTo = computed(
  () => products.activeVariant?.licence?.endDate,
)

// Ensures that showModal initial value is set correctly
await router.isReady()

const isPayRedirect = computed(
  () => route.query["pay-redirect"] === "true",
)

watch(isPayRedirect, val => {
  if (val) {
    trackPurchase()
  }
})

const hasTimedOut = useTimeout(60000)

const interval = computed(() => {
  if (products.activeVariant?.userHasLicence) {
    return false
  }

  if (!isPayRedirect.value) {
    return false
  }

  if (hasTimedOut.value) {
    return false
  }

  return 2000
})

const isLoading = computed(() => !!interval.value)

const { data: allLicences } = useLicences(interval)

const showModal = ref(isPayRedirect.value)

const removeRedirectParam = () => {
  const query = Object.assign({}, route.query)
  delete query?.["pay-redirect"]
  navigateTo(query, { replace: true })
}

const { user } = useAuth()

const { data } = useEcommerceCatalogue()

const trackPurchase = async () => {
  const today = new Date().toISOString().split("T")[0]
  const licenseToday = allLicences.value?.find(
    license => license.startDate === today,
  )

  const userIdHash = await hashString(user.value?.uid || "")
  if (licenseToday) {
    rudderanalytics.track(
      "purchase",
      {
        transaction_id: licenseToday.ean + "-" + userIdHash,
        value: getProductPrice(licenseToday, data)?.actual ?? 649,
        items: [
          { item_id: licenseToday.ean, item_name: licenseToday.name },
        ],
      },
      () => {
        console.info("Purchase event has been tracked!")
      },
    )
  }
}

const userHasLicence = computed(
  () => !!products.activeVariant?.userHasLicence,
)

const { data: checkoutRejected, isLoading: isCheckoutLoading } =
  useCheckoutPolling(hasTimedOut, 2000, isPayRedirect)

watch(showModal, val => {
  if (!val) {
    removeRedirectParam()
  }
})

const hasError = computed(
  () =>
    !isLoading.value &&
    !isCheckoutLoading.value &&
    (!userHasLicence.value || checkoutRejected.value === true),
)

const showSuccess = computed(
  () =>
    !isLoading.value &&
    !isCheckoutLoading.value &&
    userHasLicence.value &&
    !checkoutRejected.value,
)

watchEffect(async () => {
  if (showSuccess.value) {
    await content.invalidateContent()
  }
})
</script>

<template>
  <u-modal
    v-model="showModal"
    custom-class="md:max-w-[60vw] md:max-h-[80vh]"
    custom-inner-class="!md:rounded-[20px]"
    custom-top-class="items-start bg-white bg-opacity-100 md:items-center md:justify-center md:bg-portal-indigo-900 md:bg-opacity-90"
    header-class="hidden md:flex"
  >
    <section v-if="isLoading || isCheckoutLoading" class="body">
      <u-icon
        :key="1"
        name="spinner"
        class="flex justify-center m-auto stroke-red-60"
        size="xl"
      />
      <p>
        Vi sjekker status på betalingen din,
        <span class="font-bold">vennligst vent</span>...
      </p>
    </section>
    <section v-else>
      <div v-if="showSuccess" class="body">
        <div class="success" />
        <h2>Betalingen din er vellykket!</h2>
        <p>
          Du har nå tilgang til Kunne
          {{ products.activeProduct?.productTitle }} frem til slutten
          av semesteret.<br />
          <NuxtTime class="font-bold" :datetime="validTo" locale="nb-NO" date-style="long" />
        </p>
        <UButtonNew
          pill
          @click="
            () => {
              showModal = false
            }
          "
        >
          Ta meg til produktet
        </UButtonNew>
      </div>

      <div v-if="hasError" class="body">
        <h2>Ops, noe gikk galt!</h2>
        <p>
          Last inn siden på nytt, og se om du får tilgang til
          innholdet. <UContactCustomerService /> hvis problemet
          vedvarer, så hjelper vi deg.
        </p>
        <UCustomerServiceLink pill variant="filled">
          <template #prepend><UIcon name="user" /></template>
        </UCustomerServiceLink>
      </div>
    </section>
  </u-modal>
</template>

<style scoped lang="postcss">
.success {
  background-image: url("~/assets/images/success.svg");
  @apply h-[170px] w-[170px]
  animate-bounce
  bg-contain bg-no-repeat
  md:h-[200px] md:w-[200px];
}

.body {
  @apply flex flex-col items-center justify-center gap-y-10 bg-transparent pb-32 text-center text-[16px] md:text-[18px];

  h2 {
    @apply font-national2 text-[24px] font-bold leading-10 text-portal-indigo-900 md:text-[40px];
  }

  p,
  span {
    @apply max-w-md
    font-national2
    text-[20px]
    leading-normal
    md:text-[32px] md:leading-normal;
  }
}

.btn-square {
  @apply rounded-[5px]
  bg-red-80
  px-[20px]
  py-[4.5px] font-numeraWebRegular
  text-[18px]
  leading-normal
  md:hidden;
}
</style>
